import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import axios from 'axios';
import { logError } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = n => `$${Number(n||0).toLocaleString('en-US',{minimumFractionDigits:0})}`;

export default function CashFlow() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    axios.get(`${API}/cash-flow?year=${year}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => logError(e, { context: 'CashFlow.load' }));
  }, [year]);

  const chartData = (data?.monthly_data || []).map(m => ({
    month:    m.month.slice(5),   // MM
    Income:   m.income,
    Expenses: m.expenses,
    Reserve:  m.reserve_suggested,
  }));

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6 p-4">
        <div>
          <h1 className="text-3xl font-bold">{t('cash_flow.title', 'Cash Flow & Income Smoothing')}</h1>
          <p className="text-muted-foreground mt-1">{t('cash_flow.subtitle', `Monthly breakdown and reserve suggestions for ${year}.`)}</p>
        </div>

        {data && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: t('cash_flow.total_income','Total Income'),    value: fmt(data.total_income) },
                { label: t('cash_flow.total_expenses','Total Expenses'), value: fmt(data.total_expenses) },
                { label: t('cash_flow.net','Net'),                      value: fmt(data.total_net) },
                { label: t('cash_flow.tax_reserve','Tax Reserve (30%)'), value: fmt(data.reserve_suggested) },
              ].map(({label, value}) => (
                <Card key={label}><CardContent className="pt-4 text-center">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-xl font-black text-gray-900 mt-1">{value}</p>
                </CardContent></Card>
              ))}
            </div>

            {/* Reserve message */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <p className="font-semibold text-blue-900">{t('cash_flow.reserve_message','Reserve Recommendation')}</p>
                <p className="text-sm text-blue-700 mt-1">{data.reserve_message}</p>
              </CardContent>
            </Card>

            {/* Monthly chart */}
            {chartData.length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-base">{t('cash_flow.monthly_breakdown','Monthly Breakdown')}</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={chartData} margin={{top:5,right:10,left:0,bottom:5}}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{fontSize:11}} />
                      <YAxis tickFormatter={v=>`$${v/1000}K`} tick={{fontSize:11}} />
                      <Tooltip formatter={v=>fmt(v)} />
                      <Legend />
                      <Bar dataKey="Income"   fill="#16a34a" radius={[3,3,0,0]} />
                      <Bar dataKey="Expenses" fill="#dc2626" radius={[3,3,0,0]} />
                      <Bar dataKey="Reserve"  fill="#ca8a04" radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* 6-month projection */}
            <Card>
              <CardHeader><CardTitle className="text-base">{t('cash_flow.projection','6-Month Projection')}</CardTitle></CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        {[t('common.month','Month'), t('cash_flow.proj_income','Proj. Income'), t('cash_flow.proj_expenses','Proj. Expenses'), t('cash_flow.proj_net','Proj. Net'), t('cash_flow.reserve','Reserve')].map(h => (
                          <th key={h} className="pb-2 pr-4 text-xs font-semibold text-muted-foreground">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(data.six_month_projection || []).map(p => (
                        <tr key={p.month} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-medium">{p.month}</td>
                          <td className="py-2 pr-4 text-green-600">{fmt(p.projected_income)}</td>
                          <td className="py-2 pr-4 text-red-600">{fmt(p.projected_expenses)}</td>
                          <td className="py-2 pr-4 font-semibold">{fmt(p.projected_net)}</td>
                          <td className="py-2 text-amber-600">{fmt(p.reserve_suggested)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
